import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ProductFormDetailsInterface, SelectedFile } from 'types/productTypes';
import { ProductFormInitialState } from './initialState';

const initialState = ProductFormInitialState;

const ProductFormSlice = createSlice({
  name: 'ProductForm',
  initialState,
  reducers: {
    setIsEdit: (state, action) => {
      state.isEdit = action.payload;
    },
    setEditStoreId: (state, action) => {
      state.editStoreId = action.payload;
    },
    setIsDeleteImg: (state, action) => {
      state.isDeleteImg = action.payload;
    },
    apiError(state, action) {
      state.error = action.payload.message;
      state.loading = true;
      state.errorMsg = true;
    },
    apiSuccess(state, action) {
      state.error = '';
      state.success = action.payload.message;
      state.modalBackdrop = true;
      state.loading = false;
      state.errorMsg = false;
      state.showAllValidateErrors = false;
    },
    setSelectedFiles: (state, action: PayloadAction<SelectedFile[]>) => {
      state.selectedFiles = action.payload;
    },
    setAlertMessage: (state, action: PayloadAction<string | null>) => {
      state.alertMessage = action.payload;
    },
    updateProductDetails: (
      state,
      action: PayloadAction<Partial<ProductFormDetailsInterface>>
    ) => {
      state.productFormDetails = {
        ...state.productFormDetails,
        ...action.payload,
      };
    },
    resetProduct: (state) => {
      state.errorMsg = false;
      state.error = '';
      state.showAllValidateErrors = false;
      state.isEdit = false;
      state.isDeleteImg = 0;
      state.selectedFiles = [];
      state.alertMessage = null;
      state.productFormDetails = initialState.productFormDetails;
      state.isNoStock = false;
      state.isNoFragment = true;
      state.isPieces = true;
      state.displayVatPercentage = '';
      state.imgNameFromAPI = '';
      state.imgDisplay = '';
      state.productEditFormDetails = {};
      state.compositeModal = {
        activeInputIndex: null,
        isModalOpen: false,
        searchResults: [],
        searchTerm: '',
      };
      state.selectMultiTags = [];
    },
    setIsNoStock: (state, action: PayloadAction<boolean>) => {
      state.isNoStock = action.payload;
    },
    setIsNoFragment: (state, action: PayloadAction<boolean>) => {
      state.isNoFragment = action.payload;
    },
    setIsPieces: (state, action: PayloadAction<boolean>) => {
      state.isPieces = action.payload;
    },
    setDisplayVatPercentage: (state, action: PayloadAction<string>) => {
      state.displayVatPercentage = action.payload;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    setSuccess: (state, action: PayloadAction<string>) => {
      state.success = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setProductsCategoryOptions: (state, action) => {
      state.searchCategory.productsCategoryOptions = action.payload;
    },
    setIsCatOptionsLoading: (state, action) => {
      state.searchCategory.isCatOptionsLoading = action.payload;
    },
    setSupplierOptions: (state, action) => {
      state.searchSuppliers.supplierOptions = action.payload;
    },
    setIsSupplierOptionsLoading: (state, action) => {
      state.searchSuppliers.isSupplierOptionsLoading = action.payload;
    },
    setImgNameFromAPI: (state, action) => {
      state.imgNameFromAPI = action.payload;
    },
    setImgDisplay: (state, action) => {
      state.imgDisplay = action.payload;
    },
    setProductEditFormDetails: (state, action) => {
      state.productEditFormDetails = action.payload;
    },
    setPageConfig: (state, action) => {
      state.pageConfig = action.payload;
      state.productFormDetails.pageConfig = action.payload;
    },
    setActiveInputIndex: (state, action) => {
      state.compositeModal.activeInputIndex = action.payload;
    },
    setModalOpen: (state, action) => {
      state.compositeModal.isModalOpen = action.payload;
    },
    setSearchResults: (state, action) => {
      state.compositeModal.searchResults = action.payload;
    },
    setSearchTerm: (state, action) => {
      state.compositeModal.searchTerm = action.payload;
    },
    setShowAllValidateErrors: (state, action) => {
      state.showAllValidateErrors = action.payload;
    },
    setModalBackdrop: (state, action) => {
      state.modalBackdrop = action.payload;
    },
    setCountSubmit: (state) => {
      state.countSubmit += 1;
    },
    setPageFilters: (state, action) => {
      state.PageFilters = action.payload;
    },
    setPageFiltersBranch: (state, action) => {
      state.PageFilters.branch = action.payload;
    },
    clearPageFilters: (state) => {
      state.PageFilters = {
        productName: '',
        branch: [],
        category: '',
        quantityMin: '',
        quantityMax: '',
        barcode: '',
        code: '',
        supplier: '',
        tag_ids: [],
      };
    },
    setEntriesPerPage: (state, action) => {
      state.PageDetails.entriesPerPage = action.payload;
    },
    setCurrentPage: (state, action) => {
      state.PageDetails.currentPage = action.payload;
      state.PageDetails.inputPageValue = action.payload + 1;
    },
    setInputPageValue: (state, action) => {
      state.PageDetails.inputPageValue = action.payload;
    },
    setSearchListCache: (state, action) => {
      state.searchListCache = action.payload;
    },
    clearSearchListCache: (state) => {
      state.searchListCache = {
        cacheKey: null,
        latestSearchItemIds: null,
        productList: [],
        productTotal: 0,
      };
    },
    setProductOption: (state, action) => {
      state.productFormDetails.productOptions = action.payload;
    },
    setActivePriceIndex: (state, action) => {
      state.PriceModal.activePriceIndex = action.payload;
    },
    setPriceModalOpen: (state, action) => {
      state.PriceModal.priceModalOpen = action.payload;
    },
    setPriceBranchOptions: (state, action) => {
      state.PriceBranchOptions = action.payload;
    },
    setPriceBranch: (state, action) => {
      state.PriceBranch = action.payload;
    },
    setEditProductId: (state, action) => {
      state.editProductId = action.payload;
    },
    setPriceValues: (state, action) => {
      state.PriceModal.PriceValues = action.payload;
    },
    setShowAllValidatePriceErrors: (state, action) => {
      state.PriceModal.showAllValidateErrors = action.payload;
    },
    setCountPriceSubmit: (state) => {
      state.PriceModal.countSubmit += 1;
    },
    setPriceFormErrorCount: (state, action) => {
      state.PriceModal.FormErrorCount = action.payload;
    },
    setPriceEditKey: (state, action) => {
      state.PriceModal.EditKey = action.payload;
    },
    setSelectMultiTags: (state, action) => {
      state.selectMultiTags = action.payload;
    },
    setSelectMultiTagsFilter: (state, action) => {
      state.selectMultiTagsFilter = action.payload;
    },
  },
});

export const {
  setIsEdit,
  setEditStoreId,
  setIsDeleteImg,
  apiError,
  apiSuccess,
  setSelectedFiles,
  setAlertMessage,
  updateProductDetails,
  resetProduct,
  setError,
  setSuccess,
  setLoading,
  setIsNoStock,
  setIsNoFragment,
  setIsPieces,
  setDisplayVatPercentage,
  setProductsCategoryOptions,
  setIsCatOptionsLoading,
  setSupplierOptions,
  setIsSupplierOptionsLoading,
  setImgNameFromAPI,
  setImgDisplay,
  setProductEditFormDetails,
  setPageConfig,
  setActiveInputIndex,
  setModalOpen,
  setSearchResults,
  setSearchTerm,
  setShowAllValidateErrors,
  setModalBackdrop,
  setCountSubmit,
  setPageFilters,
  setPageFiltersBranch,
  clearPageFilters,
  setEntriesPerPage,
  setCurrentPage,
  setInputPageValue,
  setSearchListCache,
  clearSearchListCache,
  setProductOption,
  setActivePriceIndex,
  setPriceModalOpen,
  setPriceBranchOptions,
  setPriceBranch,
  setEditProductId,
  setPriceValues,
  setShowAllValidatePriceErrors,
  setCountPriceSubmit,
  setPriceFormErrorCount,
  setPriceEditKey,
  setSelectMultiTags,
  setSelectMultiTagsFilter,
} = ProductFormSlice.actions;

export default ProductFormSlice.reducer;
